import { types as api } from '@mesa-labs/mesa-api';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { DownloadButton, MesaColumn, MesaTable, Search, useMesaTableServerState } from '@mesa-labs/mesa-ui';

import {
  exportAllExternalClientVendors,
  useGetAllExternalClientVendorsQuery,
} from '../../redux/api/externalVendors';
import { useDispatch, useSelector } from '../../redux/hooks';
import { updateSearchTerm } from '../../redux/slices/externalClientVendors';
import { DefaultPaginationLimits } from './PaginationLimits';
import { useLocation } from 'react-router-dom';
import { asExportFilter } from '../../utils';
import { FilterControls, FilterSection } from './Filters';
import useSyncQueryStringParamsToRedux from '../../hooks/useSyncQueryStringParamsToRedux';

const ClientPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const TableWrapper = styled.div`
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const TableCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 18px;
  border-bottom: 1px solid #E5E7EB;
`;

const TableCardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13.5px;
  font-weight: 700;
  color: #111827;
`;

const TableTitleIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 5px;
  background: #E0E7FF;
`;

const TableMeta = styled.span`
  font-size: 11.5px;
  font-weight: 400;
  color: #9CA3AF;
`;

// currencyCode is a per-column fallback since ExternalClientVendorDataResponse
// carries no currency/currencyCode field of its own.
// TODO: MESA-1457: Multi-currency support
export const ExternalClientVendorMesaColumns: MesaColumn<api.ExternalClientVendorDataResponse>[] = [
  {
    id: 'partnerName',
    label: 'Partner',
    sortField: 'partnerId',
    accessorFn: (row) => api.getPartnerName(row.partnerId),
  },
  {
    key: 'externalClientId',
    sortField: 'externalClientId',
    label: 'External Client Id',
  },
  {
    key: 'externalClientName',
    sortField: 'externalClientName',
    label: 'External Client Name',
  },
  {
    key: 'externalVendorId',
    sortField: 'externalVendorId',
    label: 'External Vendor Id',
  },
  {
    key: 'externalVendorName',
    sortField: 'externalVendorName',
    label: 'External Vendor Name',
    flex: true,
  },
  {
    key: 'totalInvoiceSpendT12m',
    sortField: 'totalInvoiceSpendT12m',
    label: 'Client Total Invoice Spend (T12M)',
    type: 'currency',
    currencyCode: 'USD',
  },
  {
    key: 'servicedInvoiceSpendT12m',
    sortField: 'servicedInvoiceSpendT12m',
    label: 'Client Funded Invoice Spend (T12M)',
    type: 'currency',
    currencyCode: 'USD',
  },
  {
    key: 'defaultPaymentTermsDays',
    sortField: 'defaultPaymentTermsDays',
    label: 'Payment Terms (Days)',
    type: 'number',
  },
  {
    key: 'internalVendorState',
    sortField: 'internalVendorState',
    type: 'status',
    label: 'Internal Status',
  },
  {
    id: 'exclusionTypeAndReason',
    sortField: 'exclusionTypeAndReason',
    label: 'Exclusion Type and Reason',
    accessorFn: (row) => [row.exclusionType, row.exclusionReason].filter(Boolean).join(' - '),
  },
];

type ExternalClientVendorsPageTemplate = {
  businessEntityScope: api.BusinessEntityScope;
};

function ExternalClientVendorsPageTemplate(props: ExternalClientVendorsPageTemplate): React.ReactElement | null {
  const { businessEntityScope } = props;
  const qualifier = businessEntityScope === api.BusinessEntityScope.External ? 'external' : 'internal';
  const dispatch = useDispatch();
  const searchTerm = useSelector((state) => state.externalClientVendors.searchTerm) || undefined;
  const [searchInput, setSearchInput] = useState(searchTerm);

  useSyncQueryStringParamsToRedux({ sliceName: 'externalClientVendors' });

  const server = useMesaTableServerState({
    initialPageSize: 25,
    initialSorting: [{ id: 'totalInvoiceSpend', desc: true }],
    columns: ExternalClientVendorMesaColumns,
    persistKey: `externalClientVendorsPage-${qualifier}`,
  });

  // externalVendorsPage/Limit/SortField/SortDirection left the
  // externalClientVendors slice for useMesaTableServerState, so
  // useSyncQueryStringParamsToRedux can no longer deep-link them — bridge
  // those four URL params into the hook once on mount. Order matters here:
  // the hook's reducer resets pageIndex to 0 whenever sorting or page size
  // changes (by design, elsewhere in the app), so setPageNumber must run last
  // or a `page` param gets silently clobbered back to the first page.
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlPage = params.get('externalVendorsPage');
    const urlLimit = params.get('externalVendorsLimit');
    const urlSortField = params.get('externalVendorsSortField');
    const urlSortDirection = params.get('externalVendorsSortDirection');
    if (urlSortField) server.setSorting([{ id: urlSortField, desc: urlSortDirection === 'desc' }]);
    if (urlLimit) server.setPageSize(Number(urlLimit));
    if (urlPage) server.setPageNumber(Number(urlPage));
  }, []);

  const filter = {
    ...server.queryArgs,
    sortDirection: server.queryArgs.sortDirection as api.SortDirection | undefined,
    ...(searchTerm && {
      search: searchTerm,
    }),
    businessEntityScope,
  };

  const {
    data: {
      data: externalClientVendors = [],
      total: totalExternalClientVendors,
    } = {},
    isLoading,
    isFetching,
  } = useGetAllExternalClientVendorsQuery({
    ...filter,
  });

  return (
    <ClientPageContainer>
      <FilterSection
        filterControl={(
          <FilterControls>
            <Search
              placeholder="Search by Client Name, Vendor Name, etc.... Press Enter to search, Escape to clear."
              value={searchInput || ''}
              onChange={(value) => setSearchInput(value)}
              onSubmit={(value) => {
                dispatch(updateSearchTerm(value));
                server.setPageNumber(1);
              }}
              expanded
              width="550px"
            />
            <DownloadButton
              text="Download CSV"
              data={() => exportAllExternalClientVendors({
                ...asExportFilter(filter),
              })}
              fileName="external-client-vendors.csv"
              display="purple"
            />
          </FilterControls>
        )}
      >
      </FilterSection>

      <TableWrapper>
        <TableCardHeader>
          <TableCardTitle>
            <TableTitleIcon />
            {qualifier === 'external' ? 'External Client Vendors' : 'Internal Client Vendors'}
            {totalExternalClientVendors !== undefined && (
              <TableMeta>{totalExternalClientVendors.toLocaleString()} results</TableMeta>
            )}
          </TableCardTitle>
        </TableCardHeader>
        <MesaTable
          columns={ExternalClientVendorMesaColumns}
          data={externalClientVendors}
          dynamicColumnMode
          getRowId={(row) => `${row.partnerId}:${row.externalClientId}:${row.externalVendorId}`}
          customStyles={{
            columnFontSize: '10.5px',
            columnFontWeight: 700,
            columnColor: '#6B7280',
          }}
          getRowHref={(row) => `/partners/${row.partnerId}/${qualifier}-clients/${row.externalClientId}`}
          isLoading={isLoading || isFetching}
          {...server.bind}
          pagination={{
            ...server.bind.pagination,
            total: totalExternalClientVendors,
            pageSizeOptions: DefaultPaginationLimits.map((l) => l.value),
          }}
          columnLayout={{ enableResizing: true, enableVisibilityToggle: true, enablePinning: true }}
          minColumnWidth={90}
        />
      </TableWrapper>
    </ClientPageContainer>
  );
}

export default ExternalClientVendorsPageTemplate;
